import type { BusinessExecutionFrame } from "@/lib/assistant/text-stream";
import { getBackendClient } from "@/lib/backend";
import { firstRecord, isRecord } from "@/lib/utils/query-helpers";

/**
 * One business command as the operator should see it. Text frames supply
 * `started`/`created`/`rejected`/`unknown`/`conflict`/`waiting`; the
 * owner-scoped command list adds `ready`/`needs_input` and the rejection code;
 * voice marks a repeat question the operator turned down as `declined`. Never
 * the sealed input.
 */
export interface BusinessReceipt {
  commandId: string;
  status:
    | "started"
    | "created"
    | "rejected"
    | "conflict"
    | "unknown"
    | "waiting"
    | "needs_input"
    | "ready"
    | "declined"
    // Read live for a resumed thread's stored references.
    | "canceled"
    | "superseded"
    | "expired"
    | "unavailable";
  operation?: string;
  resource?: { kind: string; id: string };
  errorCode?: string;
  question?: string;
}

/** The recovery read after a failed or incomplete turn. */
export type BusinessReconciliation =
  | { state: "loading" }
  | { state: "unavailable" }
  | { state: "loaded"; receipts: BusinessReceipt[] };

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const OPERATION = /^[a-z]+(_[a-z]+)*\.[a-z]+(_[a-z]+)*$/;
const KIND = /^[a-z]+(_[a-z]+)*$/;
const ERROR_CODE = /^[A-Z][A-Z0-9_]{0,63}$/;
/** A deadlock rolled the attempt back: nothing saved, and the same command is safe to run. */
const EXECUTION_CONFLICT = "EXECUTION_CONFLICT";
/** A planned step that still waits was refused at execute: it is saved and never executes while it waits. */
const WAITING = "WAITING";
/** A planned step expired while it waited. An expired step cannot be promoted, so it never executes. */
const WAITING_EXPIRED = "WAITING_EXPIRED";
const OWNER_STATUS = new Map<unknown, BusinessReceipt["status"]>([
  ["completed", "created"],
  ["needs_input", "needs_input"],
  ["ready", "ready"],
  ["waiting", "waiting"],
  ["rejected", "rejected"],
]);
const RESOURCE_LINKS = new Map<string, { label: string; path: (id: string) => string }>([
  ["account", { label: "Open account", path: (id) => `/accounts/${id}` }],
  // Credits and their applications have no detail route; the list holds them.
  ["credit_application", { label: "Open customer credits", path: () => "/credits" }],
  ["customer_credit", { label: "Open customer credits", path: () => "/credits" }],
  [
    "customer_payment",
    { label: "Open customer payment", path: (id) => `/customer-payments/${id}` },
  ],
  ["estimate", { label: "Open estimate", path: (id) => `/estimates/${id}` }],
  ["expense", { label: "Open expense", path: (id) => `/expenses/${id}` }],
  ["invoice", { label: "Open invoice", path: (id) => `/invoices/${id}` }],
  ["project", { label: "Open project", path: (id) => `/projects/${id}` }],
  ["purchase_order", { label: "Open purchase order", path: (id) => `/purchase-orders/${id}` }],
  // Tasks have no detail route; the workspace lists the task and its comments.
  ["task", { label: "Open tasks", path: () => "/tasks" }],
  ["comment", { label: "Open tasks", path: () => "/tasks" }],
  ["vendor_credit", { label: "Open vendor credits", path: () => "/vendor-credits" }],
  ["vendor_credit_application", { label: "Open vendor credits", path: () => "/vendor-credits" }],
  ["vendor_payment", { label: "Open vendor payment", path: (id) => `/vendor-payments/${id}` }],
]);
/**
 * The one resource kind whose page cannot be named from the kind alone. `payment.record` now
 * names its receipt by direction and ledger row (`customer_payment`, `vendor_payment`,
 * `customer_credit`, `vendor_credit`), each with a fixed page above. Receipts stored before
 * that still say `payment`, so they keep the table lookup below.
 */
export const PAYMENT_RECORD_KIND = "payment";
/**
 * A legacy `payment` receipt's id is whichever ledger row the finance command wrote: a
 * customer or vendor payment, or the credit holding an overpayment or an estimate payment.
 * Only the id's own table says which page opens it; a payment has a detail route, a credit
 * does not.
 */
const PAYMENT_RECORD_TARGETS = [
  {
    table: "gl_customer_payments",
    label: "Open customer payment",
    path: (id: string) => `/customer-payments/${id}`,
  },
  {
    table: "gl_vendor_payments",
    label: "Open vendor payment",
    path: (id: string) => `/vendor-payments/${id}`,
  },
  { table: "gl_customer_credits", label: "Open customer credits", path: () => "/credits" },
  { table: "gl_vendor_credits", label: "Open vendor credits", path: () => "/vendor-credits" },
] as const;
const REJECTION_REASONS = new Map<string | undefined, string>([
  ["INVALID_ARGUMENT", "Its input could not be applied."],
  ["REFERENCE_UNAVAILABLE", "A referenced record is unavailable."],
  ["TARGET_CHANGED", "The record changed after the command was prepared."],
  ["VERIFY_MISMATCH", "The saved record did not match the command."],
  [WAITING_EXPIRED, "The step expired while it waited."],
]);

const backend = getBackendClient();

/**
 * A final rejection proves the command saved nothing and never will. Four
 * REJECTION_REASONS codes are persisted rejections: the command is stored as
 * rejected, rolled back, and can never run again. WAITING_EXPIRED is the
 * fifth: no rejection is stored, but a waiting step is never executed and an
 * expired one can no longer be promoted, so it is as permanent. No other code
 * at execute proves the command did not save, so any other code (or none)
 * reads as not confirmed until the command's stored status is read. In
 * particular CONVERSATION_UNAVAILABLE has five origins, among them the edge
 * refusing a duplicate delivery of an execute that the first delivery already
 * committed, and UNAVAILABLE comes from a capability pre-check that treats any
 * read failure as "unavailable". Text frames and voice results share this table.
 */
export function isFinalRejection(code: string | undefined): boolean {
  return code !== undefined && REJECTION_REASONS.has(code);
}

/**
 * How far a receipt settles its command: a saved or finally rejected command (3)
 * outranks one that may have saved (2), which outranks an attempt that is
 * running, was cleanly rolled back, or was refused because the step still
 * waits (1: nothing saved yet, and the next attempt's outcome replaces it),
 * which outranks a stored snapshot of a command not yet attempted (0, `ready`
 * or `needs_input`: an attempt still executing reads as `ready`, and a resumed
 * thread's `unavailable` reference). A resumed `canceled` or `expired` one is
 * settled with nothing saved (3). A refusal of
 * one attempt says nothing about an earlier attempt that may have committed,
 * so it never replaces a receipt ranked above it.
 */
export function receiptRank(receipt: BusinessReceipt): number {
  switch (receipt.status) {
    case "created":
    case "declined":
    case "canceled":
    case "superseded":
    case "expired":
      return 3;
    case "rejected":
      return isFinalRejection(receipt.errorCode) ? 3 : 2;
    case "unknown":
      return 2;
    case "conflict":
    case "started":
    case "waiting":
      return 1;
    default:
      return 0;
  }
}

/** True for a command that may have saved: the evidence an operator must reconcile before asking again. */
export function mayHaveSaved(receipt: BusinessReceipt): boolean {
  return receiptRank(receipt) === 2;
}

/** A newer receipt takes the place of an earlier one for its command only when at least as definitive. */
export function mayReplaceReceipt(next: BusinessReceipt, earlier: BusinessReceipt): boolean {
  return receiptRank(next) >= receiptRank(earlier);
}

/**
 * The most definitive rank each command has reached in the text transcript,
 * counting a turn's recovery read as well as its own receipts.
 */
export function strongestReceiptRanks(
  turns: readonly { receipts?: BusinessReceipt[]; reconciliation?: BusinessReconciliation }[],
): Map<string, number> {
  const ranks = new Map<string, number>();
  for (const turn of turns) {
    const recovered = turn.reconciliation?.state === "loaded" ? turn.reconciliation.receipts : [];
    for (const receipt of [...(turn.receipts ?? []), ...recovered]) {
      ranks.set(
        receipt.commandId,
        Math.max(ranks.get(receipt.commandId) ?? 0, receiptRank(receipt)),
      );
    }
  }
  return ranks;
}

/** Why a rejected command saved nothing, when the backend named a known reason. */
export function rejectionReason(code: string | undefined): string | undefined {
  return REJECTION_REASONS.get(code);
}

export interface ReceiptLink {
  label: string;
  path: string;
}

function isReceiptLink(answer: ReceiptLink | "failed" | null): answer is ReceiptLink {
  return typeof answer === "object" && answer !== null;
}

/**
 * Finds the page for a `payment.record` receipt by asking each ledger table
 * (under the operator's own access) whether it holds the id. Null when every
 * table answered and none holds it. A lookup that failed with no table holding
 * the id throws instead, so a transient failure is not remembered as "no page".
 */
export async function findPaymentRecordLink(id: string): Promise<ReceiptLink | null> {
  if (!UUID.test(id)) return null;
  const answers = await Promise.all(
    PAYMENT_RECORD_TARGETS.map(async (target): Promise<ReceiptLink | "failed" | null> => {
      try {
        const { data, error } = await backend.data
          .from(target.table)
          .select("id")
          .eq("id", id)
          .maybeSingle();
        if (error) return "failed";
        return data ? { label: target.label, path: target.path(id) } : null;
      } catch {
        return "failed";
      }
    }),
  );
  const link = answers.find(isReceiptLink) ?? null;
  if (!link && answers.includes("failed")) throw new Error("Could not look up the payment record.");
  return link;
}

export function resourceLink(
  resource: BusinessReceipt["resource"],
  operation?: string,
): ReceiptLink | null {
  if (!resource) return null;
  // An archived document or expense has no page of its own until it is restored.
  if (operation?.endsWith(".archive"))
    return { label: "Open deletion archive", path: "/admin/deletion-archive" };
  const link = RESOURCE_LINKS.get(resource.kind);
  return link ? { label: link.label, path: link.path(resource.id) } : null;
}

/**
 * A validated stream frame as a receipt. A clean conflict and a refusal because
 * the step waits are definite non-executions that are not rejections: the
 * command is still to run, and the later frame of a new attempt replaces them.
 * An expired waiting step is a final rejection (see isFinalRejection).
 */
export function receiptFromFrame(frame: BusinessExecutionFrame): BusinessReceipt {
  if (frame.status !== "rejected") return frame;
  if (frame.errorCode === EXECUTION_CONFLICT) return { ...frame, status: "conflict" };
  if (frame.errorCode === WAITING) return { ...frame, status: "waiting" };
  return frame;
}

/** A turn that ended without an answer cannot leave a command looking in progress. */
export function settleUnfinished(receipts: BusinessReceipt[] | undefined) {
  return receipts?.map((receipt) =>
    receipt.status === "started" ? { ...receipt, status: "unknown" as const } : receipt,
  );
}

function businessResource(value: unknown): BusinessReceipt["resource"] | undefined {
  if (!isRecord(value)) return undefined;
  const { id, kind } = value;
  if (typeof id !== "string" || !UUID.test(id)) return undefined;
  if (typeof kind !== "string" || !KIND.test(kind)) return undefined;
  return { id, kind };
}

/** The repeat guard's question, in the product's words rather than a raw timestamp. */
export function questionText(value: unknown): string | undefined {
  const first = Array.isArray(value) ? value[0] : undefined;
  if (!isRecord(first)) return undefined;
  if (first.id === "confirmRepeat")
    return "An identical command completed recently. Tell the assistant whether to repeat it.";
  return typeof first.prompt === "string" && first.prompt.trim()
    ? first.prompt.slice(0, 280)
    : undefined;
}

/** A completed row stores its receipt: `{ commandId, operation, resource }`. */
function completedResource(value: Record<string, unknown>) {
  const result = value.result;
  if (!isRecord(result)) return undefined;
  if (result.commandId !== value.commandId || result.operation !== value.operation)
    return undefined;
  return businessResource(result.resource);
}

function ownerReceipt(value: unknown): BusinessReceipt | null {
  if (!isRecord(value)) return null;
  const status = OWNER_STATUS.get(value.status);
  const { commandId, errorCode, operation } = value;
  if (!status || typeof commandId !== "string" || !UUID.test(commandId)) return null;
  if (typeof operation !== "string" || !OPERATION.test(operation)) return null;
  const resource = status === "created" ? completedResource(value) : undefined;
  if (status === "created" && !resource) return null;
  return {
    commandId,
    status,
    operation,
    ...(resource ? { resource } : {}),
    ...(typeof errorCode === "string" && ERROR_CODE.test(errorCode) ? { errorCode } : {}),
    ...(status === "needs_input" ? { question: questionText(value.questions) } : {}),
  };
}

const STORED_STATUS = new Map<unknown, BusinessReceipt["status"]>([
  ...OWNER_STATUS,
  ["canceled", "canceled"],
  ["declined", "declined"],
  ["superseded", "superseded"],
  ["expired", "expired"],
  ["unavailable", "unavailable"],
]);

/**
 * A stored message's command or document proposal as the server read it now
 * (`assistant-threads` `messages`), never the status at the time of the turn.
 * A proposal reads as its document's creation, keyed by the proposal id.
 */
export function storedReceipt(value: unknown): BusinessReceipt | null {
  if (!isRecord(value)) return null;
  const status = STORED_STATUS.get(value.status);
  const id = value.kind === "command" ? value.commandId : value.proposalId;
  if (!status || typeof id !== "string" || !UUID.test(id)) return null;
  const operation =
    value.kind === "command"
      ? value.operation
      : `${typeof value.documentKind === "string" ? value.documentKind : "document"}.create`;
  if (value.kind !== "command" && value.kind !== "proposal") return null;
  if (status !== "unavailable" && (typeof operation !== "string" || !OPERATION.test(operation)))
    return null;
  const resource = status === "created" ? businessResource(value.resource) : undefined;
  if (status === "created" && !resource) return null;
  const { errorCode } = value;
  return {
    commandId: id,
    status,
    ...(typeof operation === "string" && OPERATION.test(operation) ? { operation } : {}),
    ...(resource ? { resource } : {}),
    ...(typeof errorCode === "string" && ERROR_CODE.test(errorCode) ? { errorCode } : {}),
  };
}

/**
 * The owner's latest business commands across their conversations. Any
 * refusal (expired conversation, lost access, malformed page) returns null so
 * a recovery read can never turn into a second failure.
 */
export async function fetchRecentBusinessReceipts(
  conversationId: string | null,
): Promise<BusinessReceipt[] | null> {
  if (!conversationId) return null;
  try {
    const { data, error } = await backend.data.rpc("assistant_list_owner_business_operations", {
      p_conversation_id: conversationId,
    });
    const page = firstRecord(data);
    if (error || page?.ok !== true || !Array.isArray(page.operations)) return null;
    const receipts = page.operations.slice(0, 5).map(ownerReceipt);
    return receipts.every((receipt) => receipt !== null) ? receipts : null;
  } catch {
    return null;
  }
}

/**
 * Fills a turn's receipts with the operation and outcome its frames omit. The
 * stored status replaces a receipt only when at least as definitive, so a
 * `ready` snapshot (an attempt still executing reads as ready) never outranks a
 * receipt that may have saved.
 */
export function withRecentDetail(
  receipts: BusinessReceipt[],
  recent: BusinessReceipt[],
): BusinessReceipt[] {
  const known = new Map(recent.map((receipt) => [receipt.commandId, receipt]));
  return receipts.map((receipt) => {
    const detail = known.get(receipt.commandId);
    if (!detail || receipt.status === "created") return receipt;
    return mayReplaceReceipt(detail, receipt)
      ? detail
      : { ...receipt, operation: detail.operation };
  });
}
